import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ActiveDevice } from "../../api/contentDeviceApi";
import { formatFullDateString } from "../../utils/commonFunctions";

export interface DeviceLimitModalViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: string;
  /** 줄바꿈(\n)이 그대로 반영된다 */
  description: string;
  errorMessage?: string | null;

  /** 해제할 기기 목록 (선택 단계에서만 노출) */
  devices?: ActiveDevice[];
  selectedDeviceId?: number | null;
  onSelectDevice?: (deviceId: number) => void;

  confirmText: string;
  cancelText: string;
  confirmDisabled?: boolean;
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;

  /** 통합마이페이지 기기 관리 링크 (선택 단계에서만 노출) */
  manageLinkText?: string;
  onMoveToDeviceManage?: () => void;
}

/**
 * 기기 등록 초과 안내 모달 (표현 전용)
 *
 * 상태는 갖지 않으며 모든 값을 props로 받는다.
 * 실제 연결은 DeviceLimitModal이 담당한다.
 */
const DeviceLimitModalView: React.FC<DeviceLimitModalViewProps> = ({
  open,
  onOpenChange,
  title,
  description,
  errorMessage,
  devices,
  selectedDeviceId,
  onSelectDevice,
  confirmText,
  cancelText,
  confirmDisabled = false,
  isProcessing = false,
  onConfirm,
  onCancel,
  manageLinkText,
  onMoveToDeviceManage,
}) => {
  const showDeviceList = Boolean(devices?.length && onSelectDevice);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-[rgba(33,33,33,0.4)] z-50" />
        <Dialog.Content className="box-border bg-white flex flex-col gap-[24px] items-center justify-center min-w-[300px] w-[340px] overflow-hidden pb-[24px] pt-[32px] px-[24px] rounded-[16px] shadow-[0px_6px_12px_-6px_rgba(0,0,0,0.12),0px_8px_24px_-4px_rgba(0,0,0,0.08)] fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
          {/* 제목 + 안내 문구 */}
          <div className="flex flex-col gap-[12px] items-start w-full">
            <Dialog.Title className="font-bold text-[#212121] text-[16px] leading-[24px] tracking-[-0.32px] text-center w-full m-0">
              {title}
            </Dialog.Title>
            <Dialog.Description className="font-medium text-[#666] text-[14px] leading-[22px] tracking-[-0.28px] text-center w-full whitespace-pre-wrap break-keep m-0">
              {description}
            </Dialog.Description>
          </div>

          {/* 해제할 기기 선택 */}
          {showDeviceList && (
            <div
              role="radiogroup"
              aria-label="해제할 기기 선택"
              // border-solid: 전역 리셋(common.scss)이 div/span에 border:0을 걸어
              // border-style이 none이 되므로 테두리가 보이지 않는다
              className="w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-solid border-[#eee] divide-y divide-solid divide-[#eee]"
            >
              {devices?.map((device) => {
                const isSelected = device.id === selectedDeviceId;

                return (
                  <button
                    key={device.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => onSelectDevice?.(device.id)}
                    disabled={isProcessing}
                    className={`box-border flex items-center gap-[10px] w-full px-[14px] py-[12px] text-left transition-colors ${
                      isSelected ? "bg-[#f5f5f5]" : "bg-white"
                    }`}
                  >
                    <span
                      className={`shrink-0 w-[16px] h-[16px] rounded-full border-[1.5px] border-solid flex items-center justify-center ${
                        isSelected ? "border-[#5545e4]" : "border-[#ccc]"
                      }`}
                    >
                      {isSelected && (
                        <span className="w-[8px] h-[8px] rounded-full bg-[#5545e4]" />
                      )}
                    </span>
                    <span className="font-medium text-[#212121] text-[14px] leading-[20px] tracking-[-0.28px] truncate min-w-0 flex-1">
                      {device.deviceName}
                    </span>
                    <span className="shrink-0 font-medium text-[#999] text-[12px] leading-[16px] tracking-[-0.24px]">
                      최근 {formatFullDateString(device.lastUsedAt)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* 오류 안내 */}
          {errorMessage && (
            <p className="font-medium text-[#e5364b] text-[13px] leading-[18px] tracking-[-0.26px] text-center w-full m-0">
              {errorMessage}
            </p>
          )}

          {/* 버튼 영역 */}
          <div className="flex flex-col gap-[16px] items-center w-full">
            {/* 기기명 변경 등 전체 관리가 필요한 경우 */}
            {manageLinkText && onMoveToDeviceManage && (
              <button
                type="button"
                className="font-medium text-[#888] text-[13px] leading-[18px] tracking-[-0.26px] underline disabled:opacity-50"
                onClick={onMoveToDeviceManage}
                disabled={isProcessing}
              >
                {manageLinkText}
              </button>
            )}

            <div className="flex gap-[8px] items-center w-full">
              <button
                className="box-border bg-white border border-solid border-[#ddd] flex items-center justify-center h-[48px] px-[24px] rounded-[8px] flex-1 disabled:opacity-50"
                onClick={onCancel}
                disabled={isProcessing}
              >
                <span className="font-semibold text-[15px] leading-[22px] tracking-[-0.3px] text-[#666] text-center">
                  {cancelText}
                </span>
              </button>
              <button
                className="box-border bg-[#212121] flex items-center justify-center h-[48px] px-[24px] rounded-[8px] flex-1 disabled:opacity-50"
                onClick={onConfirm}
                disabled={confirmDisabled}
              >
                <span className="font-semibold text-[15px] leading-[22px] tracking-[-0.3px] text-white text-center whitespace-nowrap">
                  {confirmText}
                </span>
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DeviceLimitModalView;
